import {PcbSelectorMatcher} from "@buildwithflux/core";
import type {AnyPcbBakedNode, AnyPcbNode, IDocumentData, PcbNodesMap} from "@buildwithflux/models";
import {useCallback, useMemo} from "react";
import {useShallow} from "zustand/react/shallow";

import {getAllBakedNodes} from "modules/stores/pcb/utils";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";
import {
    computeSelectedPcbLayoutNodes as computeSelectedPcbLayoutNodesFromSelectors,
    computeSingleSelectedPcbNodeUid as computeSingleSelectedPcbNodeUidFromSelectors,
} from "../selectors/selection/derived";

// Re-export computeTerminalToNetIdMap for backwards compatibility
export {computeTerminalToNetIdMap} from "../selectors/pcb/derived";

/**
 * Hook to get the net ID that contains a given pad/terminal UID.
 * This looks up the terminal in all net nodes to find which net it belongs to.
 * Delegates to the memoized terminalToNetUidMap selector for performance.
 * @param uid The pad/terminal UID to look up
 * @returns The net UID that contains this terminal, or undefined if not found
 */
export const useContainingNetId = (uid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.document.containingNetUid(uid));
};

/**
 * Hook to get the PCB layout nodes from the document
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const usePcbLayoutNodes = (): IDocumentData["pcbLayoutNodes"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.pcbLayoutNodes));
};

/**
 * Hook to get top-level PCB layout nodes from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 * @returns Array of top-level layout nodes
 */
export const useTopLevelPcbLayoutNodes = () => {
    return useFluxServices().useDocumentStore(useShallow(select.document.topLevelPcbLayoutNodes));
};

/**
 * Hook to get top-level PCB footprint nodes from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 * @returns Array of top-level footprint nodes
 */
export const useTopLevelPcbFootprintNodes = () => {
    return useFluxServices().useDocumentStore(useShallow(select.document.topLevelPcbFootprintNodes));
};

/**
 * Hook to get count of top-level PCB layout nodes from the Zustand store
 * Returns a primitive number to avoid useShallow overhead
 * Use this instead of useTopLevelPcbLayoutNodes().length when only the count is needed
 * @returns Count of top-level layout nodes
 */
export const useTopLevelPcbLayoutNodesCount = (): number => {
    return useFluxServices().useDocumentStore(select.document.topLevelPcbLayoutNodesCount);
};

/**
 * Hook to get count of top-level PCB footprint nodes from the Zustand store
 * Returns a primitive number to avoid useShallow overhead
 * Use this instead of useTopLevelPcbFootprintNodes().length when only the count is needed
 * @returns Count of top-level footprint nodes
 */
export const useTopLevelPcbFootprintNodesCount = (): number => {
    return useFluxServices().useDocumentStore(select.document.topLevelPcbFootprintNodesCount);
};

// Re-export computeIsLockedByInheritance for backward compatibility
export {computeIsLockedByInheritance} from "../selectors/pcb/derived";

/**
 * Hook to check if a PCB node is locked by inheritance from the Zustand store.
 * A node is locked by inheritance if any of its ancestors are locked.
 * Delegates to the pcbLayoutNodes.isLockedByInheritance selector.
 * @param nodeUid The node UID to check
 * @returns True if the node is locked by inheritance
 */
export const useIsLockedByInheritance = (nodeUid: string | undefined): boolean => {
    return useFluxServices().useDocumentStore(select.pcbLayoutNodes.isLockedByInheritance(nodeUid));
};

/**
 * Hook to check if a PCB node is selected from the Zustand store.
 * Delegates to the pcbLayoutNodes.isPcbNodeSelected selector.
 * Uses Set.has() for O(1) lookup performance instead of Array.includes().
 * @param nodeUid The node UID to check
 * @returns True if the node is selected
 */
export const useIsPcbNodeSelected = (nodeUid: string): boolean => {
    return useFluxServices().useDocumentStore(select.pcbLayoutNodes.isPcbNodeSelected(nodeUid));
};

/**
 * Hook to get the top-level PCB layout node UIDs from the Zustand store
 * Delegates to the memoized topLevelPcbLayoutNodeUids selector.
 */
export const useTopLevelPcbLayoutNodeUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.pcbLayoutNodes.topLevelUids));
};

/**
 * Hook to get the root PCB layout node from the Zustand store
 * @param rootNodeId The UID of the root node (typically pcbLayoutRootNodeId)
 */
export const useRootPcbLayoutNode = (rootNodeId: string): AnyPcbNode | undefined => {
    return useFluxServices().useDocumentStore(select.pcbLayoutNodes.byUid(rootNodeId));
};

/**
 * @deprecated Import from selectors/selection/derived instead.
 * Re-exported for backward compatibility.
 */
export const computeSingleSelectedPcbNodeUid = computeSingleSelectedPcbNodeUidFromSelectors;

/**
 * Hook to get a single selected PCB node UID from the Zustand store.
 * Returns undefined if no PCB nodes are selected or if multiple are selected.
 * This hook checks both baked and unbaked nodes.
 */
export const useSingleSelectedPcbNodeUid = (): string | undefined => {
    const {documentService, useDocumentStore} = useFluxServices();
    const allPcbNodes = getAllBakedNodes(documentService);
    return useDocumentStore((state) => {
        return computeSingleSelectedPcbNodeUidFromSelectors(state.selectedObjectUids, allPcbNodes);
    });
};

/**
 * Finds the Components container node UID without subscribing to all nodes.
 * Delegates to the memoized componentsContainerNodeUid selector.
 * @returns The UID of the Components container node, or null if not found
 */
export const useComponentsContainerNode = (): string | null => {
    return useFluxServices().useDocumentStore(select.pcbLayoutNodes.componentsContainerUid);
};

/**
 * Gets selected PCB layout nodes as an array.
 * This hook computes the selected PCB node UIDs inline (checking both baked and unbaked nodes),
 * then fetches the corresponding nodes from the store.
 * Uses shallow comparison to avoid unnecessary re-renders.
 * @returns Array of selected PCB layout nodes
 */
export const useSelectedPcbLayoutNodes = (): AnyPcbBakedNode[] => {
    const {documentService, useDocumentStore} = useFluxServices();
    const allBakedNodes = getAllBakedNodes(documentService);

    return useDocumentStore(
        useShallow((state) => {
            return computeSelectedPcbLayoutNodesFromSelectors(
                state.selectedObjectUids,
                allBakedNodes,
                state.pcbLayoutNodes,
            );
        }),
    );
};

/**
 * Non-reactive access to all PCB nodes for measurement calculations.
 * This hook returns the current snapshot of all baked PCB nodes without
 * subscribing to changes, making it suitable for calculations that need
 * the current state but don't need to trigger re-renders on changes.
 *
 * WARNING: This returns a snapshot at call time. The returned value will
 * not update when PCB nodes change. Use this only in event handlers,
 * callbacks, or calculations where you need the current state at execution time.
 *
 * @returns The current map of all baked PCB nodes
 */
export const useAllPcbNodesNonReactive = (): PcbNodesMap<AnyPcbBakedNode> => {
    const {documentService} = useFluxServices();

    // Use getState() pattern to avoid reactive subscription
    // This returns the current snapshot without subscribing to changes
    return getAllBakedNodes(documentService);
};

/**
 * Hook to compute matching PCB node UIDs based on a CSS-like selector string.
 * This uses PcbSelectorMatcher to find nodes that match the given selector.
 * The result is memoized based on document data and selector changes.
 *
 * @param selector The CSS-like selector string to match nodes against
 * @returns Array of node UIDs that match the selector
 */
export const useMatchingPcbNodeUids = (selector: string): string[] => {
    const {documentService, useDocumentStore, logger} = useFluxServices();

    // Subscribe to pcbLayoutNodes, elements, and nets — PcbSelectorMatcher depends on all three
    const pcbLayoutNodes = useDocumentStore((state) => state.pcbLayoutNodes);
    const elements = useDocumentStore((state) => state.elements);
    const nets = useDocumentStore((state) => state.nets);

    return useMemo(() => {
        const documentData = useDocumentStore.getState().document;
        if (!documentData) return [];
        const allBakedNodes = getAllBakedNodes(documentService);
        return PcbSelectorMatcher.getMatchingNodeUids(documentData, allBakedNodes, selector, logger);
    }, [pcbLayoutNodes, elements, nets, documentService, useDocumentStore, selector, logger]);
};

/**
 * Non-reactive function to compute matching PCB node UIDs.
 * This is useful for event handlers that need to compute matches at execution time
 * without subscribing to state changes.
 *
 * @returns A memoized function that takes a selector and returns matching node UIDs
 */
export const useMatchingPcbNodeUidsCallback = () => {
    const {documentService, useDocumentStore, logger} = useFluxServices();

    return useCallback(
        (selector: string): string[] => {
            const documentData = useDocumentStore.getState().document;
            if (!documentData) return [];
            const allBakedNodes = getAllBakedNodes(documentService);
            return PcbSelectorMatcher.getMatchingNodeUids(documentData, allBakedNodes, selector, logger);
        },
        [documentService, useDocumentStore, logger],
    );
};
